/**
 * Safety gate form component with Vuexy styling.
 * Provides form for creating and editing safety gates with organized sections.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// MUI Imports
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Component Imports
import { TextField } from '@core/components/mui';
import { safetyGateService, SafetyGateSeverity } from '../../services/safetyGateService';
import { useNotificationStore } from '../../store/notificationStore';

/**
 * Safety gate form component for creating and editing safety gates.
 */
export const SafetyGateForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [actionPattern, setActionPattern] = useState('');
  const [severity, setSeverity] = useState<SafetyGateSeverity>(SafetyGateSeverity.Medium);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [requiredApprovers, setRequiredApprovers] = useState<number | undefined>(undefined);
  const [autoApprovalTimeoutMinutes, setAutoApprovalTimeoutMinutes] = useState<number | undefined>(undefined);
  const [approverRolesJson, setApproverRolesJson] = useState('');
  const [conditionJson, setConditionJson] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && id) {
      loadGate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadGate = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await safetyGateService.getSafetyGate(id);
      setName(data.name);
      setDescription(data.description || '');
      setActionPattern(data.actionPattern);
      setSeverity(data.severity);
      setRequiresApproval(data.requiresApproval);
      setRequiredApprovers(data.requiredApprovers);
      setAutoApprovalTimeoutMinutes(data.autoApprovalTimeoutMinutes);
      setApproverRolesJson(data.approverRolesJson || '');
      setConditionJson(data.conditionJson || '');
      setIsActive(data.isActive);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load safety gate';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!actionPattern.trim()) {
      setError('Action pattern is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        actionPattern: actionPattern.trim(),
        severity,
        requiresApproval,
        requiredApprovers: requiredApprovers || undefined,
        autoApprovalTimeoutMinutes: autoApprovalTimeoutMinutes || undefined,
        approverRolesJson: approverRolesJson.trim() || undefined,
        conditionJson: conditionJson.trim() || undefined,
        isActive,
      };

      if (isEdit && id) {
        await safetyGateService.updateSafetyGate(id, data);
        addNotification('Safety gate updated successfully', 'success');
      } else {
        await safetyGateService.createSafetyGate(data);
        addNotification('Safety gate created successfully', 'success');
      }
      navigate('/safety-gates');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save safety gate';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate('/safety-gates')} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {isEdit ? 'Edit Safety Gate' : 'Create Safety Gate'}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isEdit}
                helperText={isEdit ? 'Name cannot be changed after creation' : 'Unique name for the safety gate'}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Action Pattern (Regex)"
                value={actionPattern}
                onChange={(e) => setActionPattern(e.target.value)}
                required
                helperText="Regular expression pattern to match tool/action names"
                placeholder="e.g., ^delete|^remove|^drop"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select value={severity} label="Severity" onChange={(e) => setSeverity(e.target.value as SafetyGateSeverity)}>
                  <MenuItem value={SafetyGateSeverity.Low}>Low</MenuItem>
                  <MenuItem value={SafetyGateSeverity.Medium}>Medium</MenuItem>
                  <MenuItem value={SafetyGateSeverity.High}>High</MenuItem>
                  <MenuItem value={SafetyGateSeverity.Critical}>Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)} />
                }
                label="Requires Approval"
              />
            </Grid>

            {requiresApproval && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Required Approvers"
                    type="number"
                    value={requiredApprovers || ''}
                    onChange={(e) => setRequiredApprovers(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    helperText="Number of approvers required (leave empty for single approver)"
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Auto-Approval Timeout (minutes)"
                    type="number"
                    value={autoApprovalTimeoutMinutes || ''}
                    onChange={(e) => setAutoApprovalTimeoutMinutes(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    helperText="Auto-approve after this many minutes (optional)"
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Approver Roles (JSON array)"
                    value={approverRolesJson}
                    onChange={(e) => setApproverRolesJson(e.target.value)}
                    multiline
                    rows={2}
                    helperText='JSON array of roles, e.g., ["Admin", "Security"]'
                    placeholder='["Admin", "Security"]'
                  />
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Condition JSON (optional)"
                value={conditionJson}
                onChange={(e) => setConditionJson(e.target.value)}
                multiline
                rows={3}
                helperText="Additional conditions as JSON object"
                placeholder='{"key": "value"}'
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />
            </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 6 }}>
                  <Button variant="outlined" onClick={() => navigate('/safety-gates')}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
